import { useState, useRef, useEffect, useCallback } from 'react'
import { Play, Pause, X, RotateCcw, RotateCw } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AudioPlayerProps {
  sessionId: string
  csrName: string
  onClose: () => void
}

export function AudioPlayer({ sessionId, csrName, onClose }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const isSeekingRef = useRef(false) // Track if user is dragging the slider
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Format time as mm:ss
  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleLoadedMetadata = () => {
      setDuration(audio.duration)
      setIsLoading(false)
    }

    const handleTimeUpdate = () => {
      // Don't update during seek to avoid slider jumping
      if (!isSeekingRef.current) {
        setCurrentTime(audio.currentTime)
      }
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }

    const handleError = () => {
      setError('Failed to load recording')
      setIsLoading(false)
    }

    const handleCanPlay = () => {
      setIsLoading(false)
      // Auto-play when ready
      audio.play().then(() => setIsPlaying(true)).catch(() => {})
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)
    audio.addEventListener('canplay', handleCanPlay)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
      audio.removeEventListener('canplay', handleCanPlay)
    }
  }, [sessionId])

  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {})
    }
  }, [isPlaying])

  const handleSeekStart = useCallback(() => {
    isSeekingRef.current = true
  }, [])

  const handleSeekEnd = useCallback(() => {
    isSeekingRef.current = false
  }, [])

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return

    const newTime = parseFloat(e.target.value)
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }, [])

  const skip = useCallback((seconds: number) => {
    const audio = audioRef.current
    if (!audio) return

    const newTime = Math.max(0, Math.min(audio.currentTime + seconds, duration))
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }, [duration])

  const handleClose = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
    }
    onClose()
  }, [onClose])

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  if (error) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 rounded-xl shadow-lg px-4 py-3 flex items-center gap-3">
        <span className="text-sm text-red-600">{error}</span>
        <button
          type="button"
          onClick={handleClose}
          className="p-1 hover:bg-red-100 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-red-500" />
        </button>
      </div>
    )
  }

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 flex items-center gap-4 min-w-[400px]">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={`/dashboard/recordings/${sessionId}`}
        preload="metadata"
      />

      {/* Play/Pause */}
      <button
        type="button"
        onClick={togglePlay}
        disabled={isLoading}
        className={cn(
          'p-2 rounded-full transition-colors flex-shrink-0',
          isLoading
            ? 'bg-gray-100 text-gray-400 cursor-wait'
            : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
        )}
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        ) : isPlaying ? (
          <Pause className="w-5 h-5" />
        ) : (
          <Play className="w-5 h-5" />
        )}
      </button>

      {/* Skip back 10s */}
      <button
        type="button"
        onClick={() => skip(-10)}
        disabled={isLoading}
        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
        title="Back 10s"
      >
        <RotateCcw className="w-4 h-4" />
      </button>

      {/* Progress bar + time */}
      <div className="flex-1 flex items-center gap-3 min-w-[200px]">
        <span className="text-xs text-gray-500 w-10 text-right tabular-nums flex-shrink-0">
          {formatTime(currentTime)}
        </span>

        {/* Range slider */}
        <input
          type="range"
          min={0}
          max={duration || 100}
          step={0.1}
          value={currentTime}
          onChange={handleSeek}
          onMouseDown={handleSeekStart}
          onMouseUp={handleSeekEnd}
          onTouchStart={handleSeekStart}
          onTouchEnd={handleSeekEnd}
          disabled={isLoading}
          className="flex-1 h-2 bg-gray-200 rounded-full appearance-none cursor-pointer disabled:cursor-wait
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-4
            [&::-webkit-slider-thumb]:h-4
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-blue-600
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:shadow-md
            [&::-webkit-slider-thumb]:hover:bg-blue-700
            [&::-webkit-slider-thumb]:transition-colors
            [&::-moz-range-thumb]:w-4
            [&::-moz-range-thumb]:h-4
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-blue-600
            [&::-moz-range-thumb]:border-0
            [&::-moz-range-thumb]:cursor-pointer
            [&::-moz-range-thumb]:shadow-md"
          style={{
            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${progress}%, #e5e7eb ${progress}%, #e5e7eb 100%)`
          }}
        />

        <span className="text-xs text-gray-500 w-10 tabular-nums flex-shrink-0">
          {formatTime(duration)}
        </span>
      </div>

      {/* Skip forward 10s */}
      <button
        type="button"
        onClick={() => skip(10)}
        disabled={isLoading}
        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
        title="Forward 10s"
      >
        <RotateCw className="w-4 h-4" />
      </button>

      {/* CSR name */}
      <span className="text-sm text-gray-600 truncate max-w-[120px]" title={csrName}>
        {csrName}
      </span>

      {/* Close */}
      <button
        type="button"
        onClick={handleClose}
        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
